/* 18/09/2026 | José Luis Aragón Cervantes | Portal cliente separado del monitor Support; estilos aislados y evidencias. */
/* 18/09/2026 | José Luis Aragón Cervantes | Tablero y detalle de soporte estilo Jira. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, ThemeProvider, createTheme } from '@mui/material';
import styles from './TicketBoard.module.css';
const cx = names => names.split(/\s+/).filter(Boolean).map(name => styles[name] || name).join(' ');
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#579dff'
    },
    background: {
      paper: '#28292d'
    }
  }
});
const STATES = [{
  id: 'NUEVO',
  label: 'Por hacer'
}, {
  id: 'EN_ATENCION',
  label: 'En curso'
}, {
  id: 'EN_ESPERA',
  label: 'Esperando al cliente'
}, {
  id: 'RESUELTO',
  label: 'Resuelto'
}, {
  id: 'CERRADO',
  label: 'Cerrado'
}];
const PRIORITIES = ['BAJA', 'MEDIA', 'ALTA', 'URGENTE'];
const TYPES = {
  FALLO: 'Fallo del sistema',
  ASISTENCIA: 'Asistencia técnica',
  SOLICITUD: 'Solicitud'
};
const NEXT = {
  NUEVO: ['EN_ATENCION', 'EN_ESPERA', 'RESUELTO'],
  EN_ATENCION: ['NUEVO', 'EN_ESPERA', 'RESUELTO'],
  EN_ESPERA: ['EN_ATENCION', 'RESUELTO'],
  RESUELTO: ['EN_ATENCION', 'CERRADO'],
  CERRADO: ['EN_ATENCION']
};
const date = value => value ? new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`).toLocaleString() : '—';
const label = state => STATES.find(s => s.id === state)?.label || state;
const initials = value => (value || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase();
// 19/09/2026 | Normaliza cualquier forma de respuesta del API a arreglo para evitar
// "Cannot read properties of undefined (reading 'map')".
const asArray = value => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    if (Array.isArray(value.tickets)) return value.tickets;
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.result)) return value.result;
  }
  return [];
};
function Links({
  text
}) {
  return <div className={cx("tk-links")}>{String(text || '').split(/[\s,;]+/).filter(Boolean).map((url, i) => {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer">{url}</a>;
    })}</div>;
}
export default function TicketBoard({
  api,
  administrative = false,
  userName = ''
}) {
  const [tickets, setTickets] = useState([]),
    [agents, setAgents] = useState([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');
  const [query, setQuery] = useState(''),
    [client, setClient] = useState(''),
    [agent, setAgent] = useState(''),
    [priority, setPriority] = useState(''),
    [type, setType] = useState(''),
    [customerStatus, setCustomerStatus] = useState('');
  const [detail, setDetail] = useState(null),
    [opening, setOpening] = useState(false),
    [busy, setBusy] = useState(false),
    [drag, setDrag] = useState(''),
    [tab, setTab] = useState('conversation');
  const [message, setMessage] = useState(''),
    [links, setLinks] = useState(''),
    [internal, setInternal] = useState(false),
    [files, setFiles] = useState([]),
    [fileKey, setFileKey] = useState(0);
  const [create, setCreate] = useState(false),
    [form, setForm] = useState({
      titulo: '',
      descripcion: '',
      categoria: 'SOPORTE TECNICO',
      prioridad: 'MEDIA',
      tipoTicket: 'ASISTENCIA'
    });
  const requestId = useRef(0),
    listId = useRef(0),
    mutation = useRef(false);
  const reload = useCallback(async () => {
    const id = ++listId.current;
    try {
      const rows = await api.list();
      if (id === listId.current) setTickets(asArray(rows));
    } catch (e) {
      setError(e.message || 'No se pudieron actualizar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => {
    reload();
    const timer = setInterval(reload, 45000);
    return () => clearInterval(timer);
  }, [reload]);
  useEffect(() => {
    if (administrative) api.agents().then(rows => setAgents(asArray(rows))).catch(e => setError(e.response?.data?.message || e.message));
  }, [api, administrative]);
  const safeTickets = Array.isArray(tickets) ? tickets : [];
  const safeAgents = Array.isArray(agents) ? agents : [];
  const clients = useMemo(() => Array.from(new Map(safeTickets.map(t => [t.tenantId, t.tenantNombre || t.tenantKey || t.tenantId])).entries()), [safeTickets]);
  const filtered = useMemo(() => safeTickets.filter(t => (!query || `${t.folio} ${t.titulo} ${t.tenantNombre || ''} ${t.categoria || ''}`.toLowerCase().includes(query.toLowerCase())) && (!client || t.tenantId === client) && (!agent || (agent === 'none' ? !t.responsableId : String(t.responsableId) === agent)) && (!priority || t.prioridad === priority) && (!type || t.tipoTicket === type) && (administrative || !customerStatus || (customerStatus === 'UNREAD' ? t.noLeido : t.status === customerStatus))), [safeTickets, query, client, agent, priority, type, customerStatus, administrative]);
  const resetReply = () => {
    setMessage('');
    setLinks('');
    setInternal(false);
    setFiles([]);
    setFileKey(k => k + 1);
  };
  const open = async id => {
    const seq = ++requestId.current;
    setOpening(true);
    setError('');
    try {
      const d = await api.detail(id);
      if (seq === requestId.current) {
        setDetail(d);
        resetReply();
        setTab('conversation');
      }
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      if (seq === requestId.current) setOpening(false);
    }
  };
  const close = () => {
    if (!busy) {
      ++requestId.current;
      setDetail(null);
      resetReply();
    }
  };
  const manage = async (ticket, change) => {
    if (!administrative || mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError('');
    try {
      const d = await api.manage(ticket.ticketId, {
        status: ticket.status,
        prioridad: ticket.prioridad,
        tipoTicket: ticket.tipoTicket,
        responsableId: ticket.responsableId ?? null,
        version: ticket.version,
        ...change
      });
      setDetail(prev => prev?.ticketId === d.ticketId ? d : prev);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      await reload();
    } finally {
      mutation.current = false;
      setBusy(false);
      setDrag('');
    }
  };
  const selectFiles = e => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 5 || selected.some(f => f.size > 8 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'].includes(f.type))) {
      setError('Seleccione hasta cinco evidencias (PNG, JPG, WEBP, GIF o PDF) de máximo 8 MB cada una.');
      e.target.value = '';
      setFiles([]);
      return;
    }
    setFiles(selected);
  };
  const reply = async e => {
    e.preventDefault();
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError('');
    try {
      const d = await api.reply(detail.ticketId, {
        mensaje: message,
        enlaces: links,
        esInterno: internal,
        archivos: files
      });
      setDetail(d);
      resetReply();
      await reload();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      mutation.current = false;
      setBusy(false);
    }
  };
  const submit = async e => {
    e.preventDefault();
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError('');
    try {
      const d = await api.create({
        ...form,
        archivos: files
      });
      setCreate(false);
      setDetail(d);
      setTab('conversation');
      setForm({
        titulo: '',
        descripcion: '',
        categoria: 'SOPORTE TECNICO',
        prioridad: 'MEDIA',
        tipoTicket: 'ASISTENCIA'
      });
      resetReply();
      await reload();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      mutation.current = false;
      setBusy(false);
    }
  };
  const download = async a => {
    try {
      await api.download(detail.ticketId, a);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };
  const attachments = messageId => <div className={cx("tk-attachments")}>{(detail?.adjuntos || []).filter(a => (a.mensajeId || null) === (messageId || null)).map(a => <button type="button" key={a.adjuntoId} onClick={() => download(a)}>↧ {a.nombreArchivo}</button>)}</div>;
  const notice = error && <div className={cx("tk-error")} role="alert">{error}<button onClick={() => setError('')} aria-label="Cerrar aviso">×</button></div>;
  return <ThemeProvider theme={theme}><section className={cx("tk-app")} aria-label="Centro de asistencia">
  <header className={cx("tk-header")}><div><div className={cx("tk-overline")}>SizeSoftX / {administrative ? 'Administración de soporte' : 'Mi empresa'}</div><h1><span className={cx("tk-brand")}>✓</span>{administrative ? ' Centro de asistencia' : ' Mis solicitudes'}</h1></div><div className={cx("tk-actions")}>{!administrative && <button className={cx("tk-primary")} onClick={() => {
            resetReply();
            setCreate(true);
          }}>＋ Crear ticket</button>}<button onClick={reload} disabled={busy}>↻ Actualizar</button></div></header>
  <div className={cx("tk-nav")}><span className={cx("tk-active")}>{administrative ? "▥ Tablero de soporte" : "Mis solicitudes de soporte"}</span><span>{safeTickets.length} tickets</span><span>{safeTickets.filter(t => t.noLeido).length} sin leer</span></div>
  <div className={cx("tk-filters")}><input aria-label="Buscar tickets" placeholder={administrative ? "Buscar por título, folio o cliente" : "Buscar por título o folio"} value={query} onChange={e => setQuery(e.target.value)} />{administrative && <><select aria-label="Cliente" value={client} onChange={e => setClient(e.target.value)}><option value="">Todos los clientes</option>{clients.map(([id, name]) => <option value={id} key={id}>{name}</option>)}</select><select aria-label="Responsable" value={agent} onChange={e => setAgent(e.target.value)}><option value="">Responsables</option><option value="none">Sin asignar</option>{safeAgents.map(a => <option value={a.id} key={a.id}>{a.nombre}</option>)}</select></>}<select aria-label="Prioridad" value={priority} onChange={e => setPriority(e.target.value)}><option value="">Prioridades</option>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select><select aria-label="Tipo" value={type} onChange={e => setType(e.target.value)}><option value="">Tipos de ticket</option>{Object.entries(TYPES).map(([k, v]) => <option value={k} key={k}>{v}</option>)}</select>{!administrative && <select aria-label="Estado de mis solicitudes" value={customerStatus} onChange={e => setCustomerStatus(e.target.value)}><option value="">Todos los estados</option><option value="UNREAD">Con novedades</option>{STATES.map(st => <option key={st.id} value={st.id}>{label(st.id)}</option>)}</select>}<span className={cx("tk-muted")}>{filtered.length} resultados</span></div>
  {notice}{opening && <p role="status">Abriendo ticket…</p>}{loading ? <p role="status">Cargando tickets…</p> : administrative ? <div className={cx("tk-board")}>{STATES.map(state => {
          const rows = filtered.filter(t => t.status === state.id);
          return <section key={state.id} className={cx(`tk-column ${drag === state.id ? 'tk-drop' : ''}`)} onDragOver={e => {
            if (administrative && !busy) {
              e.preventDefault();
              setDrag(state.id);
            }
          }} onDragLeave={() => setDrag('')} onDrop={e => {
            e.preventDefault();
            setDrag('');
            const t = safeTickets.find(x => x.ticketId === e.dataTransfer.getData('text/plain'));
            if (administrative && t && t.status !== state.id) manage(t, {
              status: state.id
            });
          }}><h2>{state.label}<span>{rows.length}</span></h2><div className={cx("tk-cards")}>{rows.map(t => <button type="button" key={t.ticketId} className={cx(`tk-card ${t.noLeido ? 'tk-unread' : ''}`)} draggable={administrative && !busy} onDragStart={e => {
                e.dataTransfer.setData('text/plain', t.ticketId);
                e.dataTransfer.effectAllowed = 'move';
              }} onDragEnd={() => setDrag('')} onClick={() => open(t.ticketId)} disabled={busy}><div className={cx("tk-card-top")}><span className={cx(`tk-type tk-${t.tipoTicket}`)}>{TYPES[t.tipoTicket] || 'Asistencia'}</span>{t.noLeido && <span className={cx("tk-dot")} title="Actividad sin leer" />}</div><h3>{t.titulo}</h3>{administrative && <p className={cx("tk-client")}>{t.tenantNombre || t.tenantKey || t.tenantId}</p>}<p className={cx("tk-preview")}>{t.ultimoMensaje || t.categoria || 'Sin respuestas todavía'}</p><div className={cx("tk-card-meta")}><span className={cx(`tk-priority tk-${t.prioridad}`)}>{t.prioridad === 'URGENTE' ? '⚑' : '↑'} {t.prioridad}</span><span>◷ {date(t.fechaActualizacionUtc)}</span></div><footer><span className={cx("tk-folio")}>{t.folio}</span><span title={`${t.mensajesCount || 0} respuestas`}>☏ {t.mensajesCount || 0}</span><span className={cx("tk-avatar")} title={t.responsableNombre || 'Sin asignar'}>{initials(t.responsableNombre)}</span></footer></button>)}{rows.length === 0 && <div className={cx("tk-empty")}>Sin tickets</div>}</div>{!administrative && state.id === 'NUEVO' && <button className={cx("tk-create-inline")} onClick={() => {
              resetReply();
              setCreate(true);
            }}>＋ Crear ticket</button>}</section>;
        })}</div> : <div className={cx("tk-requests")} aria-label="Mis solicitudes">
   <p className={cx("tk-muted")}>Crea una solicitud y conversa con soporte. Aquí recibirás sus respuestas, soluciones y actualizaciones.</p>
   <div className={cx("tk-request-head")} aria-hidden="true"><span>Solicitud</span><span>Estado</span><span>Prioridad</span><span>Te atiende</span><span>Actualización</span></div>
   {filtered.map(t => <button type="button" className={cx(`tk-request-row ${t.noLeido ? 'tk-request-unread' : ''}`)} key={t.ticketId} onClick={() => open(t.ticketId)} disabled={busy}>
    <span className={cx("tk-request-subject")}><span className={cx("tk-folio")}>{t.folio}</span><strong>{t.titulo}</strong><span className={cx("tk-muted")}>{TYPES[t.tipoTicket]} · {t.categoria}</span>{t.ultimoMensaje && <span className={cx("tk-preview")}>{t.ultimoOrigen === 'SUPPORT' ? 'Soporte: ' : ''}{t.ultimoMensaje}</span>}{t.noLeido && <span className={cx("tk-new-badge")}>Nueva actualización</span>}</span>
    <span className={cx("tk-request-status")}>{label(t.status)}</span><span className={cx(`tk-priority tk-${t.prioridad}`)}>{t.prioridad}</span><span>{t.responsableNombre || 'Pendiente de asignación'}</span><span className={cx("tk-muted")}>{date(t.fechaActualizacionUtc)}</span>
   </button>)}
    {!filtered.length && <p className={cx("tk-empty")}>{safeTickets.length ? 'No hay solicitudes que coincidan con los filtros.' : 'Todavía no tienes solicitudes. Usa Crear ticket para pedir ayuda.'}</p>}
  </div>}
  <Dialog open={Boolean(detail)} onClose={close} maxWidth={false} fullWidth PaperProps={{
        className: cx('tk-dialog')
      }}><div className={cx("tk-app tk-detail")}>{detail && <><header className={cx("tk-detail-header")}><span className={cx("tk-folio")}>{detail.folio}</span><span className={cx("tk-muted")}>{TYPES[detail.tipoTicket]}</span><button onClick={close} disabled={busy} aria-label="Cerrar detalle">×</button></header>{notice}<div className={cx("tk-detail-grid")}><main className={cx("tk-main")}><h1>{detail.titulo}</h1><h2>Descripción</h2><p className={cx("tk-description")}>{detail.descripcion || 'Sin descripción.'}</p>{attachments(null)}<h2>{administrative ? "Seguimiento y soluciones" : "Respuestas y actualizaciones"}</h2><div className={cx("tk-tabs")}><button className={cx(tab === 'conversation' ? 'tk-selected' : '')} onClick={() => setTab('conversation')}>Conversación ({detail.mensajes?.length || 0})</button><button className={cx(tab === 'history' ? 'tk-selected' : '')} onClick={() => setTab('history')}>{administrative ? "Historial" : "Actualizaciones"} ({detail.historial?.length || 0})</button></div>{tab === 'history' ? <div className={cx("tk-timeline")}>{(detail.historial || []).map(h => <article key={h.eventoId}><span className={cx("tk-avatar")}>{initials(h.actor)}</span><div><strong>{h.actor}</strong><p>{h.campo}: {h.anterior || '—'} → {h.nuevo || '—'}</p><small>{date(h.fechaUtc)}</small></div></article>)}{!detail.historial?.length && <p className={cx("tk-muted")}>Aún no hay actualizaciones registradas para esta solicitud.</p>}</div> : <><div className={cx("tk-conversation")}>{(detail.mensajes || []).map(m => <article key={m.mensajeId} className={cx(m.esInterno ? 'tk-internal' : '')}><span className={cx("tk-avatar")}>{initials(m.usuario)}</span><div><strong>{m.usuario || 'Usuario'}</strong><span className={cx("tk-muted")}> · {m.origen === 'SUPPORT' ? 'Soporte' : 'Cliente'} · {date(m.fechaUtc)}</span>{m.esInterno && <span className={cx("tk-internal-label")}>NOTA INTERNA</span>}<p>{m.mensaje}</p><Links text={m.enlaces} />{attachments(m.mensajeId)}</div></article>)}{!detail.mensajes?.length && <p className={cx("tk-muted")}>Inicia la conversación de este ticket.</p>}</div>{detail.status !== 'CERRADO' ? <form onSubmit={reply} className={cx("tk-reply")}><label htmlFor="tk-message">{internal ? 'Añadir nota interna' : 'Añadir un comentario'}</label><textarea id="tk-message" value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Escribe tu respuesta…" disabled={busy} /><input aria-label="Enlaces" placeholder={administrative ? "Enlaces a videos tutoriales, manuales o soluciones" : "Enlaces de referencia para soporte"} value={links} onChange={e => setLinks(e.target.value)} disabled={busy} />{administrative && <label className={cx("tk-check")}><input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} disabled={busy} />Nota interna · solo soporte</label>}<label className={cx("tk-file")}>Adjuntar evidencias (imágenes o PDF, máx. 5 × 8 MB)<input key={fileKey} type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" onChange={selectFiles} disabled={busy} /></label><div className={cx("tk-actions")}><span className={cx("tk-muted")}>{files.length} archivos seleccionados</span><button className={cx("tk-primary")} disabled={busy || !message.trim() && !links.trim() && !files.length}>{busy ? 'Guardando…' : internal ? 'Guardar nota' : 'Enviar respuesta'}</button></div></form> : <p className={cx("tk-closed")}>Ticket cerrado. {administrative ? 'Selecciona En curso para reabrirlo.' : 'Puedes crear un nuevo ticket si necesitas más ayuda.'}</p>}</>}</main><aside className={cx("tk-sidebar")}><label>Estado{administrative ? <select value={detail.status} onChange={e => manage(detail, {
                    status: e.target.value
                  })} disabled={busy}>{STATES.map(s => <option key={s.id} value={s.id} disabled={s.id !== detail.status && !(NEXT[detail.status] || []).includes(s.id)}>{s.label}</option>)}</select> : <span className={cx("tk-status")}>{label(detail.status)}</span>}</label><div className={cx("tk-details-box")}><h2>Detalles</h2><label>Persona asignada{administrative ? <select value={detail.responsableId ?? ''} onChange={e => manage(detail, {
                      responsableId: e.target.value ? Number(e.target.value) : null
                    })} disabled={busy}><option value="">Sin asignar</option>{detail.responsableId && !safeAgents.some(a => a.id === detail.responsableId) && <option value={detail.responsableId}>{detail.responsableNombre} (inactivo)</option>}{safeAgents.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}</select> : <span>{detail.responsableNombre || 'Sin asignar'}</span>}</label><label>Prioridad{administrative ? <select value={detail.prioridad} onChange={e => manage(detail, {
                      prioridad: e.target.value
                    })} disabled={busy}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select> : <span>{detail.prioridad}</span>}</label><label>Tipo{administrative ? <select value={detail.tipoTicket} onChange={e => manage(detail, {
                      tipoTicket: e.target.value
                    })} disabled={busy}>{Object.entries(TYPES).map(([k, v]) => <option value={k} key={k}>{v}</option>)}</select> : <span>{TYPES[detail.tipoTicket]}</span>}</label>{administrative && <label>Cliente<span>{detail.tenantNombre || detail.tenantKey || detail.tenantId}</span></label>}<label>Categoría<span>{detail.categoria || 'General'}</span></label><label>Reportado por<span>{detail.usuarioCreador || userName || 'Cliente'}</span></label></div><div className={cx("tk-dates")}><p>Creado: {date(detail.fechaCreacionUtc)}</p><p>Actualizado: {date(detail.fechaActualizacionUtc)}</p>{detail.fechaCierreUtc && <p>Cerrado: {date(detail.fechaCierreUtc)}</p>}</div><button onClick={() => open(detail.ticketId)} disabled={busy}>↻ Actualizar detalle</button></aside></div></>}</div></Dialog>
  <Dialog open={create} onClose={() => {
        if (!busy) setCreate(false);
      }} maxWidth="sm" fullWidth PaperProps={{
        className: cx('tk-dialog')
      }}><div className={cx("tk-app tk-new")}><h1>Crear ticket</h1>{notice}<form onSubmit={submit}><label>Título<input required maxLength={200} value={form.titulo} onChange={e => setForm({
                ...form,
                titulo: e.target.value
              })} /></label><div className={cx("tk-form-row")}><label>Tipo<select value={form.tipoTicket} onChange={e => setForm({
                  ...form,
                  tipoTicket: e.target.value
                })}>{Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label><label>Prioridad<select value={form.prioridad} onChange={e => setForm({
                  ...form,
                  prioridad: e.target.value
                })}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></label></div><label>Categoría<select value={form.categoria} onChange={e => setForm({
                ...form,
                categoria: e.target.value
              })}>{['SOPORTE TECNICO', 'FACTURACION', 'VENTAS', 'INVENTARIOS', 'ACCESOS', 'OTRO'].map(c => <option key={c}>{c}</option>)}</select></label><label>Descripción<textarea rows={5} value={form.descripcion} onChange={e => setForm({
                ...form,
                descripcion: e.target.value
              })} /></label><label className={cx("tk-file")}>Evidencia (hasta cinco imágenes o PDF)<input key={fileKey} type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" onChange={selectFiles} /></label><div className={cx("tk-actions")}><button type="button" onClick={() => setCreate(false)} disabled={busy}>Cancelar</button><button className={cx("tk-primary")} disabled={busy || !form.titulo.trim()}>{busy ? 'Creando…' : 'Crear ticket'}</button></div></form></div></Dialog>
 </section></ThemeProvider>;
}
